export default {
  mounted() {
    this._queryElements();
    this.open = false;
    this._highlightedIndex = -1;
    this._displayText = this.input.value;

    // Click chevron toggle
    this.toggle.addEventListener("click", (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (this.open) {
        this._close();
        this.input.blur();
      } else {
        this.input.focus();
      }
    });

    // Focus opens the combobox
    this.input.addEventListener("focus", () => {
      if (!this.open) this._open();
    });

    // Typing filters options
    this.input.addEventListener("input", (e) => {
      e.stopPropagation();
      this._highlightedIndex = -1;
      this._filter();
      this._updateHighlight();
      this._pushSearch();
    });

    // Prevent bubbling to parent forms
    this.input.addEventListener("change", (e) => e.stopPropagation());

    this.input.addEventListener("keydown", (e) => {
      e.stopPropagation();
      const visible = this._visibleOptions();

      switch (e.key) {
        case "Escape":
          e.preventDefault();
          this._close();
          this.input.blur();
          break;
        case "ArrowDown":
          e.preventDefault();
          if (!this.open) {
            this._open();
            return;
          }
          if (visible.length > 0) {
            this._highlightedIndex = Math.min(
              this._highlightedIndex + 1,
              visible.length - 1,
            );
            this._updateHighlight();
          }
          break;
        case "ArrowUp":
          e.preventDefault();
          if (visible.length > 0) {
            this._highlightedIndex = Math.max(this._highlightedIndex - 1, 0);
            this._updateHighlight();
          }
          break;
        case "Enter":
          e.preventDefault();
          if (
            this._highlightedIndex >= 0 &&
            this._highlightedIndex < visible.length
          ) {
            this._select(visible[this._highlightedIndex].dataset.value);
          }
          break;
        case "Tab":
          if (this.open) this._close();
          break;
      }
    });

    // Delegate clicks on options
    this.el.addEventListener("click", (e) => {
      const option = e.target.closest("[data-ss-option]");
      if (option) {
        e.preventDefault();
        e.stopPropagation();
        this._select(option.dataset.value);
      }
    });

    document.addEventListener(
      "click",
      (this._clickAway = (e) => {
        if (this.open && !this.el.contains(e.target)) this._close();
      }),
    );
  },

  // A server-backed search patches the option list while the picker is
  // open. Keep the typed query, the open dropdown, and focus across that
  // patch. Selecting still closes first, so a selection patch stays closed.
  beforeUpdate() {
    this._wasOpen = this.open;
    this._searchText = this.input ? this.input.value : "";
    this._hadFocus = !!this.input && this.input === document.activeElement;
  },

  updated() {
    this._queryElements();
    this._displayText = this.input.dataset.display || this._displayText;

    if (this._wasOpen) {
      this.open = true;
      this.dropdown.style.display = "";
      this.input.value = this._searchText;
      this.input.removeAttribute("readonly");
      this.input.placeholder = this._displayText || "Search...";
      this.input.setAttribute("aria-expanded", "true");
      this._filter();
      this._updateHighlight();
      if (this._hadFocus) this.input.focus();
    } else {
      this._close();
    }
  },

  destroyed() {
    clearTimeout(this._searchTimer);
    document.removeEventListener("click", this._clickAway);
  },

  // With `data-search-event` set, the server owns the option list: send the
  // typed text (debounced) and let it patch the options in. The client-side
  // filter still runs on whatever is rendered, so both modes share the rest.
  _pushSearch() {
    const event = this.el.dataset.searchEvent;
    if (!event) return;
    clearTimeout(this._searchTimer);
    this._searchTimer = setTimeout(() => {
      this.pushEvent(event, { id: this.el.id, query: this.input.value });
    }, 200);
  },

  _queryElements() {
    this.input = this.el.querySelector("[data-ss-input]");
    this.toggle = this.el.querySelector("[data-ss-toggle]");
    this.dropdown = this.el.querySelector("[data-ss-dropdown]");
    this.hiddenInput = this.el.querySelector('input[type="hidden"]');
  },

  _open() {
    this.open = true;
    this._highlightedIndex = -1;
    this.dropdown.style.display = "";
    this.input.setAttribute("aria-expanded", "true");
    this.input.removeAttribute("readonly");
    this.input.value = "";
    this.input.placeholder = this._displayText || "Search...";
    this._filter();
    this._updateHighlight();
  },

  _close() {
    this.open = false;
    this._highlightedIndex = -1;
    if (this.dropdown) this.dropdown.style.display = "none";
    if (this.input) {
      this.input.value = this._displayText;
      this.input.setAttribute("aria-expanded", "false");
      this.input.setAttribute("readonly", "");
      this.input.placeholder = "";
    }
    this._filter();
    this._updateHighlight();
  },

  _filter() {
    const term =
      this.input && !this.input.readOnly ? this.input.value.toLowerCase() : "";
    this.el.querySelectorAll("[data-ss-option]").forEach((li) => {
      const label = li.dataset.label.toLowerCase();
      li.style.display = !term || label.includes(term) ? "" : "none";
    });

    // Hide group headers whose children are all hidden
    this.el.querySelectorAll("[data-ss-group-header]").forEach((header) => {
      const groupLabel = header.textContent.trim();
      const groupOptions = this.el.querySelectorAll(
        `[data-ss-option][data-ss-group="${CSS.escape(groupLabel)}"]`,
      );
      const anyVisible = Array.from(groupOptions).some(
        (opt) => opt.style.display !== "none",
      );
      header.style.display = anyVisible ? "" : "none";
    });
  },

  _visibleOptions() {
    return Array.from(this.el.querySelectorAll("[data-ss-option]")).filter(
      (li) => li.style.display !== "none",
    );
  },

  _updateHighlight() {
    const visible = this._visibleOptions();
    visible.forEach((li, i) => {
      if (i === this._highlightedIndex) {
        li.classList.add("bg-primary-600", "text-white");
        li.scrollIntoView({ block: "nearest" });
      } else {
        li.classList.remove("bg-primary-600", "text-white");
      }
    });
  },

  _select(value) {
    const name = this.hiddenInput.name;
    this.hiddenInput.value = value;

    // Find label for selected value
    const option = this.el.querySelector(
      `[data-ss-option][data-value="${CSS.escape(value)}"]`,
    );
    if (option) {
      this._displayText =
        value === "" ? this.input.dataset.prompt || "" : option.dataset.label;
    }

    this._close();
    this.input.blur();

    // If a phx-event is specified, push directly to LiveView
    const event = this.el.dataset.ssEvent;
    if (event) {
      this.pushEvent(event, { [name]: value });
    } else {
      // Fall back to input event for form-based phx-change
      this.hiddenInput.dispatchEvent(new Event("input", { bubbles: true }));
    }
  },
};
